import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface ProductCount {
  name: string;
  sku: string;
  count: number;
}

interface DailyPurchaseSummary {
  date: string;
  products: ProductCount[];
}

interface Props {
  days: DailyPurchaseSummary[];
  selectedSkus: string[];
  colors: string[];
}

export function ProductTrendChart({ days, selectedSkus, colors }: Props) {
  if (selectedSkus.length === 0 || days.length === 0) { return null; }

  // selectedSkus are now product names — skuNameMap is identity (name → name)
  const skuNameMap = new Map<string, string>(selectedSkus.map(n => [n, n]));

  // selectedSkus contains product names (stable key — see Dashboard.tsx allProducts memo)
  const chartData = days.map(d => {
    const entry: Record<string, string | number> = { date: d.date };
    selectedSkus.forEach(name => {
      const p = d.products.find(pr => pr.name === name);
      entry[name] = p ? p.count : 0;
    });
    return entry;
  });

  return (
    <div data-testid="product-trend-chart">
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickFormatter={(v: string) => v.slice(5)}
          />
          <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
          <Tooltip
            contentStyle={{ fontSize: '0.8rem' }}
            formatter={(v: any, name: any) => [v, skuNameMap.get(String(name)) ?? String(name)]}
          />
          <Legend
            formatter={(value: string) => skuNameMap.get(value) ?? value}
            wrapperStyle={{ fontSize: '0.82rem' }}
          />
          {selectedSkus.map((sku, i) => (
            <Line
              key={sku}
              type="monotone"
              dataKey={sku}
              stroke={colors[i % colors.length]}
              strokeWidth={2}
              dot={false}
              name={sku}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
